import React, { useState, useEffect, useRef } from "react";
// GRID LAYOUT
import "react-grid-layout/css/styles.css";
import GridLayout from "react-grid-layout";
// ANT DESIGN COMPONENTS
import { Spin, Tooltip } from "antd";

// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import {
  updateDetailViewLayout,
  updateTopViewLayout,
  updateStatusViewLayout,
  updateServiceViewLayout,
  updateHardCommands,
  updateAutoSortGrid,
} from "../../../redux/monitoring/monitoringSlice";
// GRID RANGES
import gridRanges from "./GridRanges";
// COMMANDS
import commands from "./commands";
// COMPONENTS
import MonitoringBank from "./MonitoringBank";
import MonitoringWebSocketConnection from "../socketConnection/MonitoringWebSocketConnection";
import MonitoringWebSocketConnectionEmulator from "../socketConnection/MonitoringWebSocketConnectionEmulator";
import MonitoringWebSocketConnectionOffline from "../socketConnection/MonitoringWebSocketConnectionOffline";
import TerminalPage from "../terminal/TerminalPage";
import ControlPanelLiftActions from "../ControlPanel/ControlPanelLiftActions";
import MonitoringDeviceView from "../deviceView/MonitoringDeviceView";
import ReplayFilesPanel from "../replayFiles/ReplayFilesPanel";
// AUTHORIZATION
import { MapViewInterface } from "../../../interfaces/MapView.interface";
import GETJwtToken from "../../../redux/authentication/authentication";
import { useLazyGetMapViewQuery } from "../../../redux/mapView/mapViewAPI";
import {
  updateMapViewArray,
  updateMapViewItem,
  updateSelectedOrganization,
} from "../../../redux/mapView/mapViewSlice";
// INTERFACES
import { ServerSocketConnectionInterface } from "../../../interfaces/EdgeManagement.interface";
import { RootState } from "../../../app/store";
import MonitoringMapView from "../mapView/MonitoringMapView";
import {
  MonitoringViewTypeEnum,
  LayoutInterface,
} from "../../../interfaces/Monitoring.interface";
// CONSTANTS
import { BLANK_LAYOUT } from "../../../constants/monitoring";

const MonitoringGrid = () => {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const liftsAllowedPerColumn = 3;
  const [t] = useTranslation("global");
  const dispatch = useDispatch();
  const READ_FREQUENCY = 10;

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [layouts, setLayouts] = useState<LayoutInterface[]>([
    { ...BLANK_LAYOUT },
  ]);
  const [mainContainerWidth, setMainContainerWidth] = useState(0);
  const [gridBanks, setGridBanks] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [colWidth, setColWidth] = useState(102);
  const [colHeight, setColHeight] = useState(90);
  const [mapViewMultipleOrgsError, setMapViewMultipleOrgsError] =
    useState(false);
  const [serverSocketConnections, setServerSocketConnections] = useState<any>(
    []
  );
  const [pendingAutoSort, setPendingAutoSort] = useState(false);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const organizationSelectMultipleData = useSelector(
    (state: RootState) => state.organization.organizationSelectMultipleData
  );
  const enableDragAndDrop = useSelector(
    (state: RootState) => state.monitoring.enableDragAndDrop
  );
  const refreshPage = useSelector(
    (state: RootState) => state.monitoring.refreshPage
  );
  const topViewLayout = useSelector(
    (state: RootState) => state.monitoring.topViewLayout
  );
  const previewFaultServiceMode = useSelector(
    (state: RootState) => state.monitoring.previewFaultServiceMode
  );
  const detailViewLayout = useSelector(
    (state: RootState) => state.monitoring.detailViewLayout
  );
  const statusViewLayout = useSelector(
    (state: RootState) => state.monitoring.statusViewLayout
  );
  const serviceViewLayout = useSelector(
    (state: RootState) => state.monitoring.serviceViewLayout
  );
  const monitoringGridType = useSelector(
    (state: RootState) => state.monitoring.monitoringGridType
  );
  const socketConnection = useSelector(
    (state: RootState) => state.monitoring.socketConnection
  );
  const openGlobalTerminal = useSelector(
    (state: RootState) => state.monitoring.openGlobalTerminal
  );
  const openReplayPanel = useSelector(
    (state: RootState) => state.monitoring.openReplayPanel
  );
  const replayFilterBankId = useSelector(
    (state: RootState) => state.monitoring.replayFilterBankId
  );
  const replayScannedBankIds = useSelector(
    (state: RootState) => state.monitoring.replayScannedBankIds
  );
  const autoSortGrid = useSelector(
    (state: RootState) => state.monitoring.autoSortGrid
  );
  const userLogged = useSelector((state: RootState) => state.home.userLogged);
  const theme = useSelector((state: RootState) => state.home.theme);

  // ************************************************ */
  // REFS ******************************************* */
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const mainContainerWidthRef = useRef(0);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [triggerGetMapView, { data: dataGetMapView }] =
    useLazyGetMapViewQuery();

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const readHardcodedCommands = () => {
    if (process.env.REACT_APP_ENVIRONMENT === "DEVELOPMENT") {
      dispatch(updateHardCommands(commands.DEVELOPMENT));
    }
    if (process.env.REACT_APP_ENVIRONMENT === "STAGING") {
      dispatch(updateHardCommands(commands.STAGING));
    }
    if (process.env.REACT_APP_ENVIRONMENT === "PRODUCTION") {
      dispatch(updateHardCommands(commands.PRODUCTION));
    }
    if (process.env.REACT_APP_ENVIRONMENT === "OFFLINE") {
      dispatch(updateHardCommands(commands.OFFLINE));
    }
    if (process.env.REACT_APP_ENVIRONMENT === "LOCAL") {
      dispatch(updateHardCommands(commands.DEVELOPMENT));
    }
  };

  const getInitialData = () => {
    readHardcodedCommands();
  };

  const getMapViewData = async (orgId: string) => {
    const token = await GETJwtToken();
    const BODY: any = {
      org_id: orgId,
    };
    triggerGetMapView({
      token,
      body: BODY,
    });
  };

  const displayIsLoading = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };

  const layoutChange = (newLayout: any) => {
    const copy = structuredClone(newLayout);
    if (monitoringGridType === MonitoringViewTypeEnum.OVER_HEAD_VIEW) {
      dispatch(updateTopViewLayout(copy));
    } else if (monitoringGridType === MonitoringViewTypeEnum.STATUS_VIEW) {
      dispatch(updateStatusViewLayout(copy));
    } else if (monitoringGridType === MonitoringViewTypeEnum.EXPANDED_VIEW) {
      dispatch(updateDetailViewLayout(copy));
    } else if (
      monitoringGridType === MonitoringViewTypeEnum.DEVICE_STATUS_VIEW
    ) {
      dispatch(updateServiceViewLayout(copy));
    }
  };

  const calculateCardWidth = (
    totalLifts: number,
    liftsAllowed: number,
    bankType: string
  ) => {
    let carWeight = 5;
    if (bankType === "GPIO") {
      return 90;
    }
    if (totalLifts === 0) {
      return 45;
    }
    gridRanges.data4.forEach((range: any) => {
      if (totalLifts === range.units) {
        carWeight = range.cardWidth;
      }
    });
    return carWeight;
  };

  const calculateCardHeight = (
    totalFloors: number,
    totalLifts: number,
    bankType: string,
    gridType: MonitoringViewTypeEnum,
    actions: any
  ) => {
    let cardHeight = 1;
    // *********************************
    if (bankType === "GPIO") {
      let totalToggle = 0;
      let totalHoldback = 0;
      let totalTurnback = 0;
      let totalToggleTimer = 0;
      actions.map((action: any) => {
        if (action && action.button_type === "TOGGLE") {
          totalToggle += 1;
        } else if (action && action.button_type === "HOLDBACK") {
          totalHoldback += 1;
        } else if (action && action.button_type === "TURNBACK") {
          totalTurnback += 1;
        } else if (action && action.button_type === "TOGGLETIMER") {
          totalToggleTimer += 1;
        }
        return true;
      });
      return (
        totalToggle * 5 +
        totalHoldback * 5 +
        totalTurnback * 5 +
        totalToggleTimer * 5
      );
    }
    //
    if (gridType === MonitoringViewTypeEnum.DEVICE_STATUS_VIEW) {
      if(bankType === "ESCALATOR" || bankType === "MOVINGWALK"){
        return 9.5
      }
      if (totalLifts <= 3) {
        return 9.7;
      }
      return totalLifts / 2;
    }
    //
    if (
      gridType === MonitoringViewTypeEnum.OVER_HEAD_VIEW 
    ) {
      if(bankType === "ESCALATOR" || bankType === "MOVINGWALK"){
        return 9.2
      }
      return 9.5;
    }
    if (gridType === MonitoringViewTypeEnum.STATUS_VIEW) {
      if(bankType === "ESCALATOR" || bankType === "MOVINGWALK"){
        return 8.2
      }
      return 8.2;
    }
    if (totalLifts === 0) {
      cardHeight = 8;
    } else {
      if(bankType === "ESCALATOR" || bankType === "MOVINGWALK"){
        return 9.7
      }
      gridRanges.data3.forEach((range: any) => {
        if (totalFloors === range.floors) {
          cardHeight = range.cardHeight;
        }
      });
    }
    return cardHeight;
  };

  const TYPE_ORDER: Record<string, number> = {
    ELEVATOR: 0,
    ESCALATOR: 1,
    MOVINGWALK: 2,
    GPIO: 3,
  };

  const getEffectiveColWidth = () => {
    if (monitoringGridType === MonitoringViewTypeEnum.OVER_HEAD_VIEW) return 6;
    if (monitoringGridType === MonitoringViewTypeEnum.STATUS_VIEW) return 5;
    if (monitoringGridType === MonitoringViewTypeEnum.EXPANDED_VIEW) return 5;
    if (monitoringGridType === MonitoringViewTypeEnum.DEVICE_STATUS_VIEW) return 5;
    if (monitoringGridType === MonitoringViewTypeEnum.MAP_VIEW) return 124;
    return colWidth;
  };

  const autoSortLayouts = (sortMode: string) => {
    if (!layouts || layouts.length <= 1) return;
    if (mainContainerWidth <= 0) return;
    displayIsLoading();
    const effectiveColWidth = getEffectiveColWidth();
    const totalCols = Math.floor(mainContainerWidth / effectiveColWidth);
    const items = structuredClone(layouts) as LayoutInterface[];

    if (sortMode === "byTypeAligned") {
      const typeGroups: Record<string, LayoutInterface[]> = {};
      items.forEach((item: any) => {
        const type = item.type || "OTHER";
        if (!typeGroups[type]) typeGroups[type] = [];
        typeGroups[type].push(item);
      });
      const sortedTypes = Object.keys(typeGroups).sort(
        (a, b) => (TYPE_ORDER[a] ?? 99) - (TYPE_ORDER[b] ?? 99)
      );
      sortedTypes.forEach((type) => {
        typeGroups[type].sort(
          (a: any, b: any) => (a.floor_count || 0) - (b.floor_count || 0)
        );
      });
      let currentY = 0;
      const result: LayoutInterface[] = [];
      sortedTypes.forEach((type) => {
        const groupItems = typeGroups[type];
        let idx = 0;
        while (idx < groupItems.length) {
          let currentX = 0;
          let rowMaxH = 0;
          const rowItems: { item: LayoutInterface; x: number }[] = [];
          while (idx < groupItems.length) {
            const item = groupItems[idx];
            if (currentX + item.w > totalCols && currentX > 0) break;
            rowItems.push({ item, x: currentX });
            currentX += item.w;
            rowMaxH = Math.max(rowMaxH, item.h);
            idx += 1;
          }
          const rowY = currentY;
          rowItems.forEach(({ item, x }) => {
            result.push({ ...item, x, y: rowY, h: rowMaxH });
          });
          currentY += rowMaxH;
        }
      });
      setLayouts(result);
      layoutChange(result);
      return;
    }

    if (sortMode === "alphabetical") {
      items.sort((a: LayoutInterface, b: LayoutInterface) =>
        (a.name || "").localeCompare(b.name || "")
      );
    } else if (sortMode === "byType") {
      items.sort((a: LayoutInterface, b: LayoutInterface) => {
        const typeA = TYPE_ORDER[a.type] ?? 99;
        const typeB = TYPE_ORDER[b.type] ?? 99;
        if (typeA !== typeB) return typeA - typeB;
        return (a.floor_count || 0) - (b.floor_count || 0);
      });
    } else {
      items.sort((a: LayoutInterface, b: LayoutInterface) => {
        if (b.h !== a.h) return b.h - a.h;
        return b.w - a.w;
      });
    }

    let currentX = 0;
    let currentY = 0;
    let rowMaxH = 0;
    const sorted = items.map((item: LayoutInterface) => {
      if (currentX + item.w > totalCols && currentX > 0) {
        currentX = 0;
        currentY += rowMaxH;
        rowMaxH = 0;
      }
      const updated = { ...item, x: currentX, y: currentY };
      currentX += item.w;
      rowMaxH = Math.max(rowMaxH, item.h);
      return updated;
    });
    setLayouts(sorted);
    layoutChange(sorted);
  };

  const updateFormSelectSavedGrid = (newArr: any, oldLayout: any) => {
    const copy = structuredClone(newArr);
    let matchCount = 0;
    copy.map((item: any, indexItem1: number) => {
      oldLayout.map((item2: any) => {
        if (item.value === item2.i) {
          copy[indexItem1].x = Math.ceil(item2.x);
          copy[indexItem1].y = Math.ceil(item2.y);
          matchCount += 1;
        }
        return true;
      });
      return true;
    });
    setLayouts(copy);
    if (matchCount === 0) {
      setPendingAutoSort(true);
    }
  };

  const generateInitialGrid = (containerWidth: number) => {
    displayIsLoading();
    let counterX = 0;
    let counterY = 0;
    const newArr: LayoutInterface[] = [];
    const banksCopy = structuredClone(gridBanks);
    banksCopy.sort(
      (a: any, b: any) =>
        parseFloat(a.bank && a.bank.floor_count ? a.bank.floor_count : 0) -
        parseFloat(b.bank && b.bank.floor_count ? b.bank.floor_count : 0)
    );
    banksCopy.map((bank: any) => {
      const totalLifts = bank && bank.lifts ? bank.lifts.length : 0;
      const totalFloors = bank && bank.floor_count ? bank.floor_count : 0;
      // Sort floors
      let floorsSorted = [];
      if (bank && bank.bank_floors) {
        floorsSorted = bank.bank_floors.sort(
          (a: any, b: any) => b.floor_index - a.floor_index
        );
      }
      // Create floors in use array
      // eslint-disable-next-line camelcase
      const floorsInUse: any = [];
      //
      newArr.push({
        ...bank,
        bank_floors: floorsSorted,
        fbEnabled: bank.fbEnabled,
        floorsInUse,
        i: bank.id,
        x: Math.ceil(counterX),
        y: counterY,
        w: calculateCardWidth(totalLifts, liftsAllowedPerColumn, bank.type),
        h: calculateCardHeight(
          totalFloors,
          totalLifts,
          bank.type,
          monitoringGridType,
          bank && bank.actions ? bank.actions : []
        ),
        l: totalLifts,
        f: totalFloors,
        floor: 3,
        start_floor_no: bank.start_floor_no,
      });
      const itemW = calculateCardWidth(totalLifts, liftsAllowedPerColumn, bank.type);
      if (monitoringGridType === MonitoringViewTypeEnum.EXPANDED_VIEW) {
        counterX += itemW;
        if (counterX > Math.floor(containerWidth / getEffectiveColWidth())) {
          counterX = 0;
          counterY += 1;
        }
      } else if (
        monitoringGridType === MonitoringViewTypeEnum.DEVICE_STATUS_VIEW
      ) {
        counterX += itemW;
        if (counterX > Math.floor(containerWidth / getEffectiveColWidth())) {
          counterX = 0;
          counterY += 1;
        }
      } else if (
        monitoringGridType === MonitoringViewTypeEnum.STATUS_VIEW ||
        monitoringGridType === MonitoringViewTypeEnum.OVER_HEAD_VIEW
      ) {
        counterX += itemW;
        if (counterX > Math.floor(containerWidth / getEffectiveColWidth())) {
          counterX = 0;
          counterY += 1;
        }
      }
      return true;
    });
    if (
      topViewLayout &&
      topViewLayout.length > 0 &&
      monitoringGridType === MonitoringViewTypeEnum.OVER_HEAD_VIEW
    ) {
      updateFormSelectSavedGrid(newArr, topViewLayout);
    } else if (
      detailViewLayout &&
      detailViewLayout.length > 0 &&
      monitoringGridType === MonitoringViewTypeEnum.EXPANDED_VIEW
    ) {
      updateFormSelectSavedGrid(newArr, detailViewLayout);
    } else if (
      statusViewLayout &&
      statusViewLayout.length > 0 &&
      monitoringGridType === MonitoringViewTypeEnum.STATUS_VIEW
    ) {
      updateFormSelectSavedGrid(newArr, statusViewLayout);
    } else if (
      serviceViewLayout &&
      serviceViewLayout.length > 0 &&
      monitoringGridType === MonitoringViewTypeEnum.DEVICE_STATUS_VIEW
    ) {
      updateFormSelectSavedGrid(newArr, serviceViewLayout);
    } else {
      setLayouts(newArr);
      setPendingAutoSort(true);
    }
  };

  // ************************************************* */
  // USE EFFECT ************************************** */
  useEffect(() => {
    getInitialData();
  }, []);

  useEffect(() => {
    if (gridBanks && gridBanks.length > 0) {
      const elementId = document.getElementById("mainContainer");
      const elementId2 = document.getElementById("mainContainer2");
      if (elementId) {
        setMainContainerWidth(elementId.clientWidth);
      } else if (elementId2) {
        generateInitialGrid(elementId2.clientWidth);
      }
    }
  }, [gridBanks]);

  useEffect(() => {
    if (
      organizationSelectMultipleData &&
      organizationSelectMultipleData.length > 0
    ) {
      setGridBanks(organizationSelectMultipleData);
      if (monitoringGridType === MonitoringViewTypeEnum.MAP_VIEW) {
        if (
          userLogged &&
          userLogged.role &&
          userLogged.role.name &&
          userLogged.role.name !== "MASTER ADMIN"
        ) {
          setMapViewMultipleOrgsError(false);
          getMapViewData(userLogged.org_id);
          dispatch(updateSelectedOrganization(userLogged.org_id));
          return;
        }
        const idOrganizationsArr = organizationSelectMultipleData
          .map((i) => i.idOrganization)
          // get an array with strings of unique values
          .filter((value, index, self) => self.indexOf(value) === index);
        if (idOrganizationsArr.length > 1) {
          setMapViewMultipleOrgsError(true);
        } else if (!idOrganizationsArr[0]) {
          setMapViewMultipleOrgsError(true);
        } else {
          setMapViewMultipleOrgsError(false);
          getMapViewData(idOrganizationsArr[0]);
          dispatch(updateSelectedOrganization(idOrganizationsArr[0]));
        }
      }
    } else {
      setGridBanks([]);
      setLayouts([]);
    }
  }, [organizationSelectMultipleData, monitoringGridType]);

  useEffect(() => {
    const startMapView = dataGetMapView?.data?.find(
      (m: MapViewInterface) => m.start === true
    );
    dispatch(updateMapViewItem(startMapView || dataGetMapView?.data[0]));
    dispatch(updateMapViewArray(dataGetMapView?.data));
  }, [dataGetMapView]);

  useEffect(() => {
    if (refreshPage) {
      displayIsLoading();
    }
  }, [refreshPage]);

  useEffect(() => {
    if (autoSortGrid) {
      autoSortLayouts(autoSortGrid);
      dispatch(updateAutoSortGrid(""));
      setPendingAutoSort(false);
    }
  }, [autoSortGrid]);

  // Deferred auto-sort: waits until layouts and mainContainerWidth are
  // committed to state (next render cycle) before dispatching the sort.
  useEffect(() => {
    if (pendingAutoSort && layouts && layouts.length > 0 && mainContainerWidth > 0) {
      dispatch(updateAutoSortGrid("byTypeAligned"));
    }
  }, [pendingAutoSort, layouts, mainContainerWidth]);

  useEffect(() => {
    const elementId = document.getElementById("mainContainer");
    if (elementId && gridBanks && gridBanks.length > 0) {
      generateInitialGrid(elementId.clientWidth);
    }
    //
    if (monitoringGridType === MonitoringViewTypeEnum.OVER_HEAD_VIEW) {
      setColHeight(1);
      setColWidth(6);
    } else if (monitoringGridType === MonitoringViewTypeEnum.STATUS_VIEW) {
      setColHeight(1);
      setColWidth(5);
    } else if (monitoringGridType === MonitoringViewTypeEnum.EXPANDED_VIEW) {
      setColHeight(1);
      setColWidth(5);
    } else if (
      monitoringGridType === MonitoringViewTypeEnum.DEVICE_STATUS_VIEW
    ) {
      setColHeight(1);
      setColWidth(5);
    } else if (monitoringGridType === MonitoringViewTypeEnum.MAP_VIEW) {
      setColHeight(90);
      setColWidth(124);
    }
  }, [monitoringGridType]);

  useEffect(() => {
    setServerSocketConnections([]);
    if (layouts && layouts.length > 0) {
      const serversId: ServerSocketConnectionInterface[] = [];
      const uniqueServerId: string[] = [];
      const firstLiftByBank: any = [];
      layouts.map((layout: LayoutInterface) => {
        if (layout && layout.lifts && layout.lifts[0]) {
          firstLiftByBank.push({
            serverId: layout.idServer,
            lift: layout.lifts[0],
          });
        }
        if (!uniqueServerId.includes(layout.idServer)) {
          serversId.push({
            id: layout.idServer,
            name: layout.nameServer,
            channelType: layout.typeChannel,
            firstLiftId: [],
            channelId: layout.channel_id,
          });
          uniqueServerId.push(layout.idServer);
        }
        return true;
      });
      const copy: any = structuredClone(serversId);
      copy.map((item: any, index: number) => {
        const newArray: any = [];
        firstLiftByBank.map((item2: any) => {
          if (
            item2 &&
            item2.serverId &&
            item &&
            item.id &&
            item.id === item2.serverId
          ) {
            newArray.push(item2.lift);
          }
          return true;
        });
        copy[index].firstLiftId = newArray;
        return true;
      });
      setServerSocketConnections(copy);
    }
  }, [layouts]);

  // Keep the ref in sync so the ResizeObserver closure always sees the latest width
  useEffect(() => {
    mainContainerWidthRef.current = mainContainerWidth;
  }, [mainContainerWidth]);

  // Recalculate grid width when the main container resizes (e.g. drawer open/close)
  useEffect(() => {
    const el = document.getElementById("mainContainer");
    if (!el) return undefined;

    resizeObserverRef.current = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        const newWidth = entry.contentRect.width;
        if (newWidth > 0 && newWidth !== mainContainerWidthRef.current) {
          setMainContainerWidth(newWidth);
        }
      }
    });
    resizeObserverRef.current.observe(el);

    return () => {
      resizeObserverRef.current?.disconnect();
    };
  }, []);

  // Regenerate grid when both mainContainerWidth and gridBanks are available.
  // Watches both deps so a late-arriving gridBanks (after width is set) or
  // a late-arriving width (after gridBanks is set) both trigger generation.
  useEffect(() => {
    if (mainContainerWidth > 0 && gridBanks && gridBanks.length > 0) {
      generateInitialGrid(mainContainerWidth);
    }
  }, [mainContainerWidth, gridBanks]);

  // ************************************************ */
  // FILTERED LAYOUTS FOR REPLAY ******************** */
  const gridLayouts = (() => {
    if (!openReplayPanel) return layouts;
    if (replayFilterBankId) {
      return layouts.filter((l) => l.idBank === replayFilterBankId);
    }
    if (replayScannedBankIds.length > 0) {
      return layouts.filter((l) => replayScannedBankIds.includes(l.idBank));
    }
    return layouts;
  })();

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <Spin spinning={isLoading}>
      {/** ************************************************** */}
      {/** MONITORING GLOBAL TERMINAL */}
      <div id="mainContainer2">
        {openGlobalTerminal && <TerminalPage layouts={layouts} />}
      </div>
      {/** ************************************************** */}
      {/** MONITORING REPLAY FILE */}
      {openReplayPanel && <ReplayFilesPanel layouts={layouts} />}
      {/** ************************************************** */}
      {/** EDGE SERVER CONNECTION */}
      {!openReplayPanel &&
        (process.env.REACT_APP_ENVIRONMENT === "DEVELOPMENT" ||
          process.env.REACT_APP_ENVIRONMENT === "PRODUCTION") && (
          <>
            {socketConnection === "EDGE" && (
              <div className="generalStyles__flexWrap">
                {serverSocketConnections.map(
                  (serverSocketConnection: ServerSocketConnectionInterface) => {
                    // Filter layouts for this specific server
                    const serverLayouts = layouts.filter(
                      (layout) => layout.idServer === serverSocketConnection.id
                    );
                    return (
                      <div key={serverSocketConnection.id}>
                        <MonitoringWebSocketConnection
                          serverId={serverSocketConnection.id}
                          serverName={serverSocketConnection.name}
                          channelType={serverSocketConnection.channelType}
                          liftName=""
                          liftId=""
                          channelId={serverSocketConnection.channelId}
                          layouts={serverLayouts}
                        />
                      </div>
                    );
                  }
                )}
              </div>
            )}
          </>
        )}
      {/** *********************************************************** */}
      {/** OFFLINE SERVER CONNECTION */}
      {!openReplayPanel && process.env.REACT_APP_ENVIRONMENT === "OFFLINE" && (
        <>
          {socketConnection === "OFFLINE" && (
            <div className="generalStyles__flexWrap">
              {serverSocketConnections.map(
                (serverSocketConnection: ServerSocketConnectionInterface) => {
                  // Filter layouts for this specific server
                  const serverLayouts = layouts.filter(
                    (layout) => layout.idServer === serverSocketConnection.id
                  );
                  return (
                    <div key={serverSocketConnection.id}>
                      <MonitoringWebSocketConnectionOffline
                        serverId={serverSocketConnection.id}
                        serverName={serverSocketConnection.name}
                        channelId={serverSocketConnection.channelId}
                        layouts={serverLayouts}
                      />
                    </div>
                  );
                }
              )}
            </div>
          )}
        </>
      )}
      {/** *********************************************************** */}
      {/** EMULATOR CONNECTION */}
      {!openReplayPanel && process.env.REACT_APP_ENVIRONMENT === "STAGING" && (
        <>
          {socketConnection === "EMULATOR" && (
            <div className="generalStyles__flexWrap">
              <MonitoringWebSocketConnectionEmulator
                serverName="311 Wacker"
                readFrequency={READ_FREQUENCY}
              />
            </div>
          )}
        </>
      )}
      {/** *********************************************************** */}
      {/** MONITORING GRID */}
      {!openGlobalTerminal && (
        <div id="mainContainer" className="monitoring__100pxBox">
          {mainContainerWidth > 0 && layouts && layouts.length > 0 && !pendingAutoSort && (
            <div>
              {monitoringGridType === MonitoringViewTypeEnum.MAP_VIEW && (
                <>
                  {mapViewMultipleOrgsError ? (
                    <div className="generalStyles__flexCenter mt-3">
                      <span className="mt-4">
                        {t("monitoring.mapViewMultipleOrgsError")}
                      </span>
                    </div>
                  ) : (
                    <MonitoringMapView layouts={gridLayouts} />
                  )}
                </>
              )}
              {monitoringGridType ===
                MonitoringViewTypeEnum.DEVICE_STATUS_VIEW && (
                  <MonitoringDeviceView layouts={gridLayouts} />
                )}
              {(monitoringGridType === MonitoringViewTypeEnum.EXPANDED_VIEW ||
                monitoringGridType === MonitoringViewTypeEnum.STATUS_VIEW ||
                monitoringGridType ===
                MonitoringViewTypeEnum.OVER_HEAD_VIEW) && (
                  <GridLayout
                    className="layout"
                    layout={gridLayouts}
                    onLayoutChange={layoutChange}
                    cols={Math.max(1, Math.floor(mainContainerWidth / getEffectiveColWidth()))}
                    rowHeight={colHeight}
                    autoSize
                    width={mainContainerWidth}
                    isResizable={false}
                    margin={[20, 20]}
                    containerPadding={[10, 10]}
                    isDraggable={enableDragAndDrop}
                    allowOverlap={false}
                  >
                    {gridLayouts.map((layout: LayoutInterface) => (
                      <div id={layout.i} key={layout.i} style={{border: (previewFaultServiceMode || enableDragAndDrop) ? "solid 1px hsl(217, 84%, 52%, 25%)" : "", borderRadius: "5px"}}>
                        {/** *********************************************************** */}
                        {/** BODY */}
                        {layout.type === "GPIO" ? (
                          <div style={{ padding: "10px" }}>
                            <ControlPanelLiftActions
                              isControlPanel={false}
                              bankId={layout.idBank}
                              bankName={layout.name}
                            />
                          </div>
                        ) : (
                          <>
                            {layout && layout.lifts && layout.lifts.length > 0 ? (
                              <div style={{ height: "100%" }}>
                                <MonitoringBank
                                  bankId={layout.idBank}
                                  channelId={layout.idChannel}
                                  serverId={layout.idServer}
                                  h={layout.h}
                                  w={layout.w}
                                />
                              </div>
                            ) : (
                              <div
                                className={`monitoring__card__${theme} generalStyles__width100`}
                                style={{ height: "100%", display: "flex", flexDirection: "column" }}
                              >
                                <div className="monitoring__card__header">
                                  <div className="generalStyles__flex">
                                    <div className="generalStyles__textEllipsis">
                                      <Tooltip placement="topLeft" title={layout.name}>
                                        <h6>{layout.name}</h6>
                                      </Tooltip>
                                    </div>
                                  </div>
                                </div>
                                <div
                                  style={{
                                    flex: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <span style={{ opacity: 0.5 }}>
                                    {t("edgeManagement.lifts.noLiftsFound")}
                                  </span>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </GridLayout>
                )}
            </div>
          )}
        </div>
      )}
    </Spin>
  );
};

export default MonitoringGrid;
